import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  changeSecondaryReimbursementAccountNumber,
  changeSecondaryReimbursementAccountType,
  changeSecondaryReimbursementConfirmAccountNumber,
  changeSecondaryReimbursementRoutingNumber,
  changeSecondaryReimbursementToExisting,
} from '../../../../../actions';
import { REIMBURSEMENT_METHODS } from '../../../../../constants';

export const useSecondaryDirectDepositContent = ({ onCancel = () => { } }) => {
  const dispatch = useDispatch();
  const [routingNumberError, setRoutingNumberError] = useState('');
  const [accountNumberError, setAccountNumberError] = useState('');
  const [
    confirmAccountNumberError,
    setConfirmAccountNumberError,
  ] = useState('');
  const { t } = useTranslation('editReimbursementMethod');

  const store = useSelector(({
    billingPayments,
    personalInformation,
  }) => ({ billingPayments, personalInformation }), shallowEqual);
  const { billingPayments } = store;
  const {
    reimbursementData: { secondaryMethod },
  } = billingPayments;

  const handleAccountTypeChange = (type) => () => {
    dispatch(changeSecondaryReimbursementAccountType(type));
  };

  // For more information about the Routing validation see:
  // http://en.wikipedia.org/wiki/Routing_transit_number
  const isRoutingNumberValid = useCallback((routing) => {
    if (routing.length !== 9) {
      return t('errors.mustBeANineDigitNumber');
    }

    const checksumTotal =
      (
        7 * (parseInt(routing.charAt(0), 10)
          + parseInt(routing.charAt(3), 10)
          + parseInt(routing.charAt(6), 10))
      )
      + (
        3 * (parseInt(routing.charAt(1), 10)
          + parseInt(routing.charAt(4), 10)
          + parseInt(routing.charAt(7), 10))
      )
      + (
        9 * (parseInt(routing.charAt(2), 10)
          + parseInt(routing.charAt(5), 10)
          + parseInt(routing.charAt(8), 10))
      );

    const checksumMod = checksumTotal % 10;
    if (checksumTotal === 0 || checksumMod !== 0) {
      return t('errors.invalidBankRoutingNumber');
    }

    return '';
  }, [t]);

  const validateNumbersOnly = useCallback((input) => {
    const ONLY_NUMBER_REGEX = /^\d+$/g;

    if (input && input.match(ONLY_NUMBER_REGEX)) {
      return '';
    }

    return t('errors.mustContainNumbersOnly');
  }, [t]);

  const isBankFormInvalid = useMemo(() => {
    if (secondaryMethod.Type === REIMBURSEMENT_METHODS.check) {
      return false;
    }

    if (routingNumberError
      || accountNumberError
      || confirmAccountNumberError
      || !secondaryMethod.AccountNumber
      || !secondaryMethod.ConfirmAccountNumber
      || !secondaryMethod.RoutingNumber) {
      return true;
    }

    return false;
  }, [
    accountNumberError,
    confirmAccountNumberError,
    secondaryMethod,
    routingNumberError,
  ]);

  const validateRoutingNumber = useCallback((routingNumber, formValidation) => {
    const numberValidation = validateNumbersOnly(routingNumber);

    if (formValidation && !numberValidation) {
      const routingNumValidation = isRoutingNumberValid(routingNumber);

      if (routingNumValidation) {
        setRoutingNumberError(routingNumValidation);
        return;
      }

      setRoutingNumberError('');
      return;
    }

    setRoutingNumberError(numberValidation);
  }, [isRoutingNumberValid, validateNumbersOnly]);

  const handleRoutingNumberChange = useCallback((event) => {
    const { target } = event;

    dispatch(changeSecondaryReimbursementRoutingNumber(target.value));
    validateRoutingNumber(target.value, target.validity.valid);
  }, [validateRoutingNumber, dispatch]);

  const validateConfirmAccountNumber = useCallback((confirmAccountNumber) => {
    if (!confirmAccountNumber.length) {
      setConfirmAccountNumberError(t('errors.mustContainNumbersOnly'));
      return;
    }

    if (confirmAccountNumber === secondaryMethod.AccountNumber) {
      setConfirmAccountNumberError('');
      return;
    }

    setConfirmAccountNumberError(t('errors.confirmAccountNumberNoMatch'));
  }, [t, secondaryMethod]);

  const validateConfirmAccountNumberManual = useCallback((confirmAccountNumber,
    accountNumber) => {
    if (confirmAccountNumber === accountNumber) {
      setConfirmAccountNumberError('');
      return;
    }
    setConfirmAccountNumberError(t('errors.confirmAccountNumberNoMatch'));
  }, [t]);

  const validateAccountNumber = useCallback((accountNumber, formValidation) => {
    const numberValidation = validateNumbersOnly(accountNumber);

    if (formValidation && !numberValidation) {
      setAccountNumberError('');
      return;
    }
    setAccountNumberError(numberValidation);
  }, []);

  const handleAccountNumberChange = useCallback((event) => {
    const { target } = event;

    dispatch(changeSecondaryReimbursementAccountNumber(target.value));
    validateAccountNumber(target.value, target.validity.valid);

    if (secondaryMethod.ConfirmAccountNumber) {
      validateConfirmAccountNumberManual(
        secondaryMethod.ConfirmAccountNumber, target.value,
      );
    }
  }, [validateAccountNumber, dispatch, secondaryMethod]);

  const handleConfirmAccountNumberChange = useCallback((event) => {
    const { target } = event;

    dispatch(changeSecondaryReimbursementConfirmAccountNumber(target.value));
    validateConfirmAccountNumber(target.value);
  }, [dispatch, validateAccountNumber, validateConfirmAccountNumber]);

  const handleEditReimbursementCancel = useCallback(() => {
    setRoutingNumberError('');
    setAccountNumberError('');
    setConfirmAccountNumberError('');

    if (onCancel) {
      onCancel();
    }
  }, []);

  const handleExistingCheckbox = useCallback((value) => () => {
    dispatch(changeSecondaryReimbursementToExisting(value));
  }, [dispatch]);

  return ({
    accountNumberError,
    confirmAccountNumberError,
    handleAccountNumberChange,
    handleAccountTypeChange,
    handleConfirmAccountNumberChange,
    handleEditReimbursementCancel,
    handleExistingCheckbox,
    handleRoutingNumberChange,
    isBankFormInvalid,
    routingNumberError,
    secondaryMethod,
  });
};
